/* eslint-disable i18next/no-literal-string */
import classNames from 'classnames'
import type { ReactNode } from 'react'

export type BiBadgeVariant =
  | 'neutral'
  | 'info'
  | 'success'
  | 'warning'
  | 'error'
  // A session's moment: live is happening now (a pulsing red dot), soon is
  // about to start (a still dot). Use filled on dark surfaces.
  | 'live'
  | 'soon'
  // A label on a dark panel: a white chip with charcoal words.
  | 'inverse'
// xxs and xs are cases-only (breakin has sm, md, lg): the navigation's Beta
// pill and the deckbuilder use them.
export type BiBadgeSize = 'xxs' | 'xs' | 'sm' | 'md' | 'lg'

// Soft look: light tint, dark text.
const VARIANTS: Record<BiBadgeVariant, string> = {
  neutral:
    'bg-bi-surface-dim text-bi-on-surface-var border-bi-outline-medium font-medium',
  info: 'bg-bi-light-teal text-bi-charcoal border-bi-deep-teal/30 font-medium',
  success:
    'bg-bi-success-soft text-bi-charcoal border-bi-deep-green/30 font-medium',
  warning:
    'bg-bi-warning-soft text-bi-charcoal border-bi-deep-gold/30 font-medium',
  error: 'bg-bi-error-soft text-bi-charcoal border-bi-error/30 font-medium',
  live: 'bg-white text-bi-on-surface border-bi-outline-medium font-bold',
  soon: 'bg-white text-bi-on-surface border-bi-outline-medium font-bold',
  inverse: 'bg-white text-bi-charcoal border-white font-semibold',
}

// Filled look: solid fill, white text (every pairing clears WCAG AA). Warning
// keeps charcoal on accent yellow: white on yellow is about 1.5:1.
const FILLED_VARIANTS: Record<BiBadgeVariant, string> = {
  neutral: 'bg-bi-charcoal text-white border-bi-charcoal font-medium',
  info: 'bg-bi-deep-teal text-white border-bi-deep-teal font-medium',
  success: 'bg-bi-deep-green text-white border-bi-deep-green font-medium',
  warning: 'bg-bi-accent text-bi-charcoal border-bi-deep-gold/40 font-medium',
  error: 'bg-bi-error text-white border-bi-error font-medium',
  live: 'bg-white/10 text-white border-white/15 font-bold',
  soon: 'bg-white/10 text-white border-white/15 font-bold',
  inverse: 'bg-white text-bi-charcoal border-white font-semibold',
}

const SIZES: Record<BiBadgeSize, string> = {
  xxs: 'px-1 py-0 text-[8px] leading-3',
  xs: 'px-1.5 py-0 text-[9px] leading-4',
  sm: 'px-1.5 py-0.5 text-xs',
  md: 'px-2 py-0.5 text-xs',
  lg: 'gap-2 px-3 py-1 text-sm',
}

// The leading dot on a soft badge is the variant's deep shade. On a filled
// badge that shade is the fill itself (or too close to it), so the dot takes
// the badge's text color instead (fixed from breakin, whose filled dots vanish).
const DOT_COLORS: Record<BiBadgeVariant, string> = {
  neutral: 'bg-bi-on-surface-var',
  info: 'bg-bi-deep-teal',
  success: 'bg-bi-deep-green',
  warning: 'bg-bi-bronze',
  error: 'bg-bi-error',
  live: 'bg-bi-error',
  soon: 'bg-bi-bronze',
  inverse: 'bg-bi-charcoal',
}

type BiBadgeProps = {
  text: ReactNode
  variant?: BiBadgeVariant
  size?: BiBadgeSize
  pill?: boolean
  filled?: boolean
  removable?: boolean
  onRemove?: () => void
  icon?: ReactNode
  /** A leading dot in the variant's deep shade. live and soon always have one. */
  dot?: boolean
  className?: string
}

/**
 * A status chip, ported from breakin's Ui::BadgeComponent. A <span> on
 * purpose: badges sit inside text and table cells, where only inline elements
 * are valid.
 */
export function BiBadge({
  text,
  variant = 'neutral',
  size = 'md',
  pill = false,
  filled = false,
  removable = false,
  onRemove,
  icon,
  dot = false,
  className,
}: BiBadgeProps) {
  // No text: nothing, never an empty bordered chip.
  if (text === null || text === undefined || text === '') return null

  return (
    <span
      className={classNames(
        'inline-flex items-center gap-1 border',
        (filled ? FILLED_VARIANTS : VARIANTS)[variant],
        SIZES[size],
        pill ? 'rounded-full' : 'rounded',
        className
      )}
    >
      <LeadingDot variant={variant} filled={filled} dot={dot} />
      {icon}
      {text}
      {removable && (
        <button
          type="button"
          onClick={onRemove}
          // Inherits the badge's text color, soft or filled. 80%, not
          // breakin's 70%: soft neutral at 70% is 2.85:1, under the 3:1 a
          // control needs.
          className="bi-focus-ring -mr-0.5 ml-0.5 rounded-sm opacity-80 transition-opacity hover:opacity-100"
          aria-label="Remove"
        >
          <svg
            className="h-3 w-3"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth="2"
            aria-hidden="true"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M6 18L18 6M6 6l12 12"
            />
          </svg>
        </button>
      )}
    </span>
  )
}

function LeadingDot({
  variant,
  filled,
  dot,
}: {
  variant: BiBadgeVariant
  filled: boolean
  dot: boolean
}) {
  // A red dot with a soft halo growing out of it; still under reduced motion.
  if (variant === 'live') {
    return (
      <span
        className="relative flex size-[0.4375rem] shrink-0"
        aria-hidden="true"
      >
        <span className="absolute inline-flex size-full rounded-full bg-bi-error motion-safe:animate-bi-live-pulse" />
        <span className="relative inline-flex size-[0.4375rem] rounded-full bg-bi-error" />
      </span>
    )
  }
  if (variant === 'soon') {
    return (
      <span
        className={classNames(
          'size-[0.4375rem] shrink-0 rounded-full',
          filled ? 'bg-bi-accent' : DOT_COLORS.soon
        )}
        aria-hidden="true"
      />
    )
  }
  if (!dot) return null
  return (
    <span
      className={classNames(
        'size-1.5 shrink-0 rounded-full',
        filled ? 'bg-current' : DOT_COLORS[variant]
      )}
      aria-hidden="true"
    />
  )
}
